
/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react';
import WorkspaceLayout from 'components/my_map/workspace/WorkspaceLayout';
import Calculator from 'components/atoms/calculator/Calculator';
import PopulationMap from 'features/lab2/components/map/layers/population/PopulationMap';
import HousingMap from 'features/lab2/components/map/layers/housing/HousingMap';
import LifestyleMap from 'features/lab2/components/map/layers/lifestyle/LifestyleMap';
import PopulationControlPanel from 'features/lab2/components/panels/PopulationControlPanel';
import HousingControlPanel from 'features/lab2/components/panels/HousingControlPanel';
import LifestyleControlPanel from 'features/lab2/components/panels/LifestyleControlPanel';
import usePopulationAppState from 'features/lab2/hooks/usePopulationAppState';
import useHousingAppState from 'features/lab2/hooks/useHousingAppState';
import useLifestyleAppState from 'features/lab2/hooks/useLifestyleAppState';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded';
import { Stack } from '@mui/material';

const MAP_TABS = [
    { id: 'population', label: '인구 지도', icon: GroupsRoundedIcon },
    { id: 'housing', label: '주택 지도', icon: HomeWorkRoundedIcon },
    { id: 'lifestyle', label: '생활 지도', icon: LocalHospitalRoundedIcon },
];

const LabMapsPage = ({ activeTab: controlledTab, onTabChange, initialTab = 'population' }) => {
    const [internalTab, setInternalTab] = useState(initialTab);
    const activeTab = controlledTab ?? internalTab;
    const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);

    const populationState = usePopulationAppState();
    const housingState = useHousingAppState();
    const lifestyleState = useLifestyleAppState();

    const views = {
        population: {
            sidebar: (
                <PopulationControlPanel
                    currentFilter={populationState.currentFilter}
                    onFilterChange={(value) => populationState.handleFilterChange(0, value)}
                    selectedAge={populationState.currentAge}
                    onAgeChange={populationState.setCurrentAge}
                    onSearch={() => populationState.searchPopulation(populationState.currentAge)}
                    onReset={() => {
                        populationState.setCurrentAge('all');
                        populationState.resetFeature();
                    }}
                />
            ),
            content: (
                <div style={{ flex: 1, minHeight: 0 }}>
                    <PopulationMap
                        selectedAge={populationState.currentAge}
                        selectedFilter={populationState.currentFilter}
                        lineLayerVisible={populationState.lineLayerVisible}
                        setLineLayerVisible={populationState.setLineLayerVisible}
                    />
                </div>
            ),
        },
        housing: {
            sidebar: (
                <HousingControlPanel
                    currentFilterValue={housingState.currentFilter?.value}
                    onFilterChange={(value) => housingState.handleFilterChange(0, value)}
                    areaRange={housingState.currentAreaRange}
                    onAreaRangeChange={housingState.setCurrentAreaRange}
                    housingTypes={housingState.currentHousingTypes}
                    onHousingTypeChange={(key, checked) =>
                        housingState.setCurrentHousingTypes((prev) => {
                            const trueCount = Object.values(prev).filter(v => v).length;

                            if (!checked && trueCount === 1 && prev[key] === true) {
                                return prev;
                            }

                            return {
                                ...prev,
                                [key]: checked,
                            };
                        })
                    }
                    onSearch={housingState.searchHousing}
                    onReset={() => {
                        housingState.setCurrentAreaRange([15, 150]);
                        housingState.setCurrentHousingTypes({
                            apartment: true,
                            multiFamily: true,
                            officetel: true,
                        });
                    }}
                />
            ),
            content: (
                <div style={{ flex: 1, minHeight: 0 }}>
                    <HousingMap pointParams={housingState.pointParams} selectedFilter={housingState.currentFilter} />
                </div>
            ),
        },
        lifestyle: {
            sidebar: (
                <LifestyleControlPanel
                    activeType={lifestyleState.type || 'hos'}
                    onTypeChange={lifestyleState.setType}
                    radius={lifestyleState.radius}
                    onRadiusChange={lifestyleState.setRadius}
                    onAreaSelect={lifestyleState.handleMapAreaSelect}
                    onAreaReset={lifestyleState.handleMapAreaReset}
                />
            ),
            content: (
                <div style={{ flex: 1, minHeight: 0 }}>
                    <LifestyleMap
                        isExpanded={lifestyleState.isTabExpanded}
                        isMapAreaSelectMode={lifestyleState.isMapAreaSelectMode}
                        selectedLocation={lifestyleState.selectedLocation}
                        setSelectedLocation={lifestyleState.setSelectedLocation}
                        onAreaReset={lifestyleState.handleMapAreaReset}
                        setIsMapAreaSelectMode={lifestyleState.setIsMapAreaSelectMode}
                        radius={lifestyleState.radius}
                        type={lifestyleState.type}
                        admCd={lifestyleState.admCd}
                    />
                </div>
            ),
        },
    };

    const activeView = views[activeTab] || views.population;

    const handleTabChange = (nextTab) => {
        if (!views[nextTab]) return;
        if (controlledTab === undefined) {
            setInternalTab(nextTab);
        }
        onTabChange?.(nextTab);
    };

    return (
        <>
            <WorkspaceLayout
                tabs={MAP_TABS}
                activeTab={activeTab}
                onTabChange={handleTabChange}
                sidebarContent={<Stack spacing={2}>{activeView.sidebar}</Stack>}
                showCalculatorButton
                onCalculatorOpen={() => setIsCalculatorOpen(true)}
            >
                {activeView.content}
                <Calculator isOpen={isCalculatorOpen} onClose={() => setIsCalculatorOpen(false)} />
            </WorkspaceLayout>
        </>
    );
};

export default LabMapsPage;
